import { Component, ViewChild, AfterViewInit, ElementRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { itemService } from '../../../helpers/services/main.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { artPieceService } from '../../../helpers/services/art_piece.service';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { ItemLibraryService } from 'src/app/helpers/services/item_library.service';
import { LoaderService } from '../../../helpers/services/loader.service';

@Component({
    selector: 'app-art-piece-detail',
    imports: [
        CommonModule,
        MatTabsModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        CarouselModule,
        ImageUploadComponent,
        MatChipsModule
    ],
    templateUrl: './art-piece-detail.component.html',
    styleUrls: ['./art-piece-detail.component.scss']
})
export class ArtPieceDetailComponent implements AfterViewInit, OnInit {
  editing: boolean = false;
  artPieceData: any;
  artPieceId: number;
  artPieceArtNumber: string;
  artPieceAttachments: any[] = [];
  relatedItems: any[] = [];
  primaryImage:string;
  divisions:any = [];
  
  licensorOptions: any = [];
  bigThemeOptions: any = [];
  propertyOptions: any = [];
  littleThemeOptions: any = [];
  seasonptions: any[] = [];
  styleguidOptions: any = [];
  artistOptions: any = [];
  ageGroupOptions: any[] = [];
  artTypeOptions: any[] = [];
  artSourceOptions: any[] = [];
  // Tags (keywords)
  tags: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER];

  selectedLicensor: any = { title: null };
  selectedBigTheme: any = { title: null };
  selectedProperty: any = { title: null };
  selectedLittleTheme: any = { title: null };
  selectedSeason: any = [];
  selectedStyleguide: any = { title: null };
  selectedArtist: any = { title: null };
  selectedAgeGroup: any = { title: null };
  selectedArtType: any = [];
  selectedArtSource: any = [];
  selectedDivision: any = [];

  customOptions: OwlOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: true,
    navSpeed: 700,
    navText: ['<', '>'],
    responsive: {
      0: {
        items: 1
      }
    },
    nav: true
  }

  constructor(
    private route: ActivatedRoute,
    private itemService: itemService,
    private artPieceService: artPieceService,
    private itemLibraryService: ItemLibraryService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private loaderService: LoaderService
  ) {
    this.artPieceId = this.route.snapshot.params['id'];
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  async ngOnInit() {
    // Start loader immediately to prevent blank page flash
    this.loaderService.increment();

    try {
      this.seasonptions = (await this.itemService.getSeasonCode().toPromise() as any).data;
      this.divisions = await this.itemService.getCompanyCode().toPromise();
      await this.getArtPieceInfo();
      await this.getRelatedItems();
    } catch (error) {
      // Error handling is done in individual methods
    } finally {
      // Turn off loader after all loading is complete
      this.loaderService.decrement();
    }
  }

  async getArtPieceInfo() {
    try {
      const artPiece: any = await this.artPieceService.getArtPiece(this.artPieceId).toPromise();
  
      this.artPieceArtNumber = artPiece.art_number;
      this.artPieceData = [artPiece]; 
  
      this.artPieceAttachments = this.artPieceData[0]?.artPieceAttachments.filter(att => {
        if (att?.primary_image) this.primaryImage = att?.uuid;
        return att.display_type !== 'Thumb';
      });
  
      const divisionCode = this.artPieceData[0].divisioncode_id;
      this.selectedDivision = this.divisions[0]?.divisionCodes.find(div => div?.divCode_id === divisionCode);

      // Load and process merch groups - wait for completion before sorting and setting form values
      const merchGroupData: any = await this.itemService.getMerchGroup({ divisionCode: [divisionCode], DivisionStatus: 1 }).toPromise();
      this.processMerchGroups(merchGroupData);
  
      // These methods now run after merch groups are loaded
      this.sortOptions();
      this.setInitialFormValues();
  
      // Parse tags into chip list
      const tagsCsv = this.artPieceData[0]?.tags || '';
      this.tags = tagsCsv ? tagsCsv.split(',').map((t: string) => t.trim()).filter(Boolean) : [];
  
    } catch (error) {
      console.error('Error loading art piece info:', error);
    }
  }

  /**
   * Processes merch group data and populates the respective option arrays
   */
  private processMerchGroups(data: any[]): void {
    this.licensorOptions = [];
    this.bigThemeOptions = [];
    this.propertyOptions = [];
    this.littleThemeOptions = [];
    this.styleguidOptions = [];
    this.artTypeOptions = [];
    this.artSourceOptions = [];
    this.artistOptions = [];
    this.ageGroupOptions = [];

    for (const item of data) {
      for (const header of item.merchGroupHeaders || []) {
        const mgTypeDesc = header.mgTypeDesc;
        const merchGroups = header.merchGroups || [];

        if (mgTypeDesc === "Licensor") {
          for (const merchGroup of merchGroups) {
            this.licensorOptions.push({
              title: merchGroup.mg_desc,
              id: merchGroup.mg_id,
              mg_code: merchGroup.mg_code,
              is_active: merchGroup.is_active,
              parent_id: merchGroup.parent_id
            });
          }
        } else if (mgTypeDesc === "Big Theme") {
          for (const merchGroup of merchGroups) {
            this.bigThemeOptions.push({
              title: merchGroup.mg_desc,
              id: merchGroup.mg_id,
              mg_code: merchGroup.mg_code
            });
          }
        } else if (mgTypeDesc === "Property") {
          for (const merchGroup of merchGroups) {
            this.propertyOptions.push({
              title: merchGroup.mg_desc,
              id: merchGroup.mg_id,
              mg_code: merchGroup.mg_code,
              is_active: merchGroup.is_active,
              parent_id: merchGroup.parent_id
            });
          }
        } else if (mgTypeDesc === "Little Theme") {
          for (const merchGroup of merchGroups) {
            this.littleThemeOptions.push({
              title: merchGroup.mg_desc,
              id: merchGroup.mg_id,
              mg_code: merchGroup.mg_code
            });
          }
        } else if (mgTypeDesc === "Style guide") {
          // Style guide processing - currently empty, can be added later if needed
        } else if (mgTypeDesc === "Art Type") {
          for (const merchGroup of merchGroups) {
            if (merchGroup.is_active) {
              this.artTypeOptions.push({
                title: merchGroup.mg_desc,
                id: merchGroup.mg_id,
                mg_code: merchGroup.mg_code
              });
            }
          }
        } else if (mgTypeDesc === "Art Source") {
          for (const merchGroup of merchGroups) {
            if (merchGroup.is_active) {
              this.artSourceOptions.push({
                title: merchGroup.mg_desc,
                id: merchGroup.mg_id,
                mg_code: merchGroup.mg_code
              });
            }
          }
        } else if (mgTypeDesc === "Artist") {
          for (const merchGroup of merchGroups) {
            if (merchGroup.is_active) {
              this.artistOptions.push({
                title: merchGroup.mg_desc,
                id: merchGroup.mg_id,
                mg_code: merchGroup.mg_code
              });
            }
          }
        } else if (mgTypeDesc === "Age Group") {
          for (const merchGroup of merchGroups) {
            if (merchGroup.is_active) {
              this.ageGroupOptions.push({
                title: merchGroup.mg_desc,
                id: merchGroup.mg_id,
                mg_code: merchGroup.mg_code
              });
            }
          }
        }
      }
    }
  }

  async getRelatedItems() {
    try {
      const items: any = await this.itemLibraryService.getItemsByDsnRefNum(this.artPieceArtNumber).toPromise();
      this.relatedItems = items || [];
    } catch (error) {
      this.relatedItems = [];
    }
  }

  customSort(field) {
    return field.sort((a, b) => {
      const valA = a?.mgCode2 || a?.mg_code || a?.title || a?.name || a?.seasonDesc || '';
      const valB = b?.mgCode2 || b?.mg_code || b?.title || b?.name || b?.seasonDesc || '';
      return valA?.toLowerCase().localeCompare(valB?.toLowerCase());
    });
  }
  
  sortOptions() {
    // Sort all options using customSort function
    this.licensorOptions = this.customSort(this.licensorOptions);
    this.bigThemeOptions = this.customSort(this.bigThemeOptions);
    this.propertyOptions = this.customSort(this.propertyOptions);
    this.littleThemeOptions = this.customSort(this.littleThemeOptions);
    this.styleguidOptions = this.customSort(this.styleguidOptions);
    this.artSourceOptions = this.customSort(this.artSourceOptions);
    this.ageGroupOptions = this.customSort(this.ageGroupOptions);
    this.artistOptions = this.customSort(this.artistOptions);
    this.artTypeOptions = this.customSort(this.artTypeOptions);
    this.seasonptions = this.customSort(this.seasonptions);
  }

  setPrimaryImage(attachment: any) {
    if (attachment.primary_image) {
      this._snackBar.open('This is already the primary image.', 'Close', { duration: 3000 });
      return;
    }
    this.artPieceService.setPrimaryImage(this.primaryImage, attachment.uuid).subscribe(res => {
      this._snackBar.open('Primary image updated successfully!', 'Close', { duration: 3000 });
      this.getArtPieceInfo();
    }, err => {
      this._snackBar.open('Error setting primary image.', 'Close', { duration: 3000 });
    });
  }

  deleteImage(attachment: any) {
    if (!confirm('Are you sure you want to delete this image?')) return;

    this.loaderService.increment();
    
    let fullFileName = attachment.file_name;
    let baseFileName = fullFileName;
    if (fullFileName.startsWith('images-')) {
       baseFileName = fullFileName.replace('images-', '');
    } else if (fullFileName.startsWith('thumbs-')) {
       baseFileName = fullFileName.replace('thumbs-', '');
    }

    const imageFileName = `images-${baseFileName}`;
    const thumbFileName = `thumbs-${baseFileName}`;
    const filesToDelete = [imageFileName, thumbFileName];
    const params = { Bucket: 'dflowbucket', files: filesToDelete };

    this.itemService.deleteBucketObjects(params).subscribe(
      () => {
        this.artPieceService.deleteAttachment(attachment.uuid).subscribe(
          () => {
             this._snackBar.open('Image deleted successfully!', 'Close', { duration: 3000 });
             this.getArtPieceInfo(); // Refresh images list
             this.loaderService.decrement();
          },
          err => {
             this._snackBar.open('Error deleting attachment record.', 'Close', { duration: 3000 });
             this.loaderService.decrement();
          }
        );
      },
      err => {
         this._snackBar.open('Error deleting image files from bucket.', 'Close', { duration: 3000 });
         this.loaderService.decrement();
      }
    );
  }

  setInitialFormValues() {
    if (!this.artPieceData || !this.artPieceData[0]) {
      return;
    }

    const currentArtPiece = this.artPieceData[0];
    const isSpruceNonLic = currentArtPiece.divisioncode_id === 9;

    const findOptionById = (options, id) => options.find(option => option.id === id);
   // const findOptionByMgId = (options, id) => options.find(option => option.mg_id === id);

    if (!isSpruceNonLic) {
        this.selectedLicensor = findOptionById(this.licensorOptions, currentArtPiece.licensor_id);
        this.selectedProperty = findOptionById(this.propertyOptions, currentArtPiece.property_id);
    } else {
        this.selectedBigTheme = findOptionById(this.bigThemeOptions, currentArtPiece.big_theme_id);
        this.selectedLittleTheme = findOptionById(this.littleThemeOptions, currentArtPiece.little_theme_id);
    }
    
    this.selectedStyleguide = findOptionById(this.styleguidOptions, currentArtPiece.style_guide_id);
    this.selectedArtist = findOptionById(this.artistOptions, currentArtPiece.artist_id);
    this.selectedAgeGroup = findOptionById(this.ageGroupOptions, currentArtPiece.age_group_id);
    this.selectedArtType = findOptionById(this.artTypeOptions, currentArtPiece.art_type_id);
    this.selectedArtSource = findOptionById(this.artSourceOptions, currentArtPiece.art_source_id);
    this.selectedSeason = this.seasonptions.find(x => x.id === currentArtPiece.season_code_id);
  }

  onUploadComplete(event: { sharedLink: string, thumbSharedLink: string }) {
    const attachmentData = {
      imageLink: event.sharedLink,
      thumbImageLink: event.thumbSharedLink,
      companyCode: this.divisions[0]?.comCode_id
    };

    this.artPieceService.addAttachment(this.artPieceId, attachmentData).subscribe(res => {
      this._snackBar.open('Image uploaded successfully!', 'Close', { duration: 3000 });
      this.getArtPieceInfo(); // Refresh data to show the new image
    }, err => {
      this._snackBar.open('Error uploading image.', 'Close', { duration: 3000 });
    });
  }

  onSave() {
    if (!this.artPieceData || !this.artPieceData[0]) {
      this._snackBar.open('No art piece data to save.', 'Close', { duration: 3000 });
      return;
    }

    const artPieceToSave = this.artPieceData[0];

    // Mapping selections to the data object
    artPieceToSave.licensor_id = this.selectedLicensor?.id;
    artPieceToSave.property_id = this.selectedProperty?.id;
    artPieceToSave.big_theme_id = this.selectedBigTheme?.id;
    artPieceToSave.little_theme_id = this.selectedLittleTheme?.id;
    artPieceToSave.style_guide_id = this.selectedStyleguide?.id;
    artPieceToSave.artist_id = this.selectedArtist?.id;
    artPieceToSave.age_group_id = this.selectedAgeGroup?.id;
    artPieceToSave.art_type_id = this.selectedArtType?.id;
    artPieceToSave.art_source_id = this.selectedArtSource?.id;
    artPieceToSave.season_code_id = this.selectedSeason?.id;

    // Persist tags as CSV string
    artPieceToSave.tags = this.tags.join(',');

    this.artPieceService.updateArtPiece(artPieceToSave).subscribe(
      res => {
        this.editing = false;
        this._snackBar.open('Art piece saved successfully!', 'Close', { duration: 3000 });
        this.getArtPieceInfo(); // Refresh data
      },
      err => {
        this._snackBar.open('Error saving art piece.', 'Close', { duration: 3000 });
      }
    );
  }

  goToItem(itemId: number) {
    window.open(`/apps/itemDetail/${itemId}`, '_blank');
  }

  getPrimaryImage(item: any): string | null {
    if (item.itemAttachments && item.itemAttachments.length > 0) {
      const primary = item.itemAttachments.find(att => att.primary_image);
      return primary ? primary.attachment_link : null;
    }
    return null;
  }

  addTag(event: any): void {
    const value = (event?.value || '').trim();
    if (value) {
      this.tags.push(value);
    }
    if (event?.input) {
      event.input.value = '';
    }
  }

  removeTag(tag: string): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }
} 